import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { usePortalSlot } from '../hooks/usePortalSlot';
import { Tabs } from '../components/ui/tabs';
import QueueMetrics from '../components/QueueMetrics';
import QueueJobsList from '../components/QueueJobsList';
import AdminSettings from '../components/AdminSettings';
import BlueprintUsage from '../components/BlueprintUsage';
import { useAuth } from '../contexts/AuthContext';

const TABS = [
  { value: 'overview', label: 'Overview' },
  { value: 'jobs', label: 'Queue Jobs' },
  { value: 'blueprint-usage', label: 'Blueprint Usage' },
  { value: 'system', label: 'System Settings' },
];

const QueueAdmin: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [isLoading, setIsLoading] = useState(true);
  // The four tabs render as a segmented switch, right-aligned on the actions
  // row next to the page subtitle (AppLayout's per-page header), matching
  // the locked mockup — not underline tabs (bd deepcontext-2951).
  const actionsSlot = usePortalSlot('app-page-actions');

  useEffect(() => {
    // Check if user is admin
    if (!user || user.role !== 'admin') {
      navigate('/');
      return;
    }
    setIsLoading(false);
  }, [user, navigate]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent mx-auto"></div>
          <p className="text-ink-muted mt-4">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full min-h-0 gap-4">
      {actionsSlot &&
        createPortal(<Tabs tabs={TABS} value={activeTab} onChange={setActiveTab} />, actionsSlot)}

      {/* Queue Jobs alone needs viewport-bound height for its table-fill
          contract (bd-n3uyh, deepcontext-wbra); the other tabs keep
          scrolling their own content (now internally, same as before when
          the whole page scrolled). */}
      {activeTab === 'overview' && (
        <div className="flex-1 min-h-0 overflow-y-auto"><QueueMetrics /></div>
      )}
      {activeTab === 'jobs' && (
        <div className="flex-1 min-h-0 flex flex-col">
          <QueueJobsList />
        </div>
      )}
      {activeTab === 'blueprint-usage' && (
        <div className="flex-1 min-h-0 overflow-y-auto"><BlueprintUsage /></div>
      )}
      {activeTab === 'system' && (
        <div className="flex-1 min-h-0 overflow-y-auto"><AdminSettings /></div>
      )}
    </div>
  );
};

export default QueueAdmin;
